import { Link } from "react-router";

import type {
  FlowRegionOwner,
  FlowSettingView,
  ProjectFlowSettings,
} from "@/api/flowSettings";
import {
  FLOW_STATE_LABEL,
  priorityText,
  priorityTrackOf,
  stagedKey,
  wayBack,
} from "@/lib/flowSettings";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";

import type { PathwayFlowSurface } from "./PathwayFlow";

/**
 * #855's explicit addition to the Lovable screen: the routing order, the
 * fallback priorities, and the obsolete keys the source recipe removes.
 *
 * ## WHY IT EXISTS AT ALL
 *
 * Lovable shows a SELECTION and no ORDER. The pinned loader routes on both: the
 * dynamic phase is a numbered list of slots read top to bottom, and an
 * automatic slot is filled from `track_priority` in ascending order. An author
 * who can see which pathways are always assessed but not in what position, or
 * which pathway a classification falls back to, cannot tell `[ptsd, auto,
 * auto]` from `[auto, auto, auto]` — and those are different clinical
 * configurations.
 *
 * ## WHY IT IS A DISCLOSURE
 *
 * Because it is evidence, and `ProjectSectionPane`'s rule is that a clinical
 * author meets the rows first. It is collapsed, compact, below the groups, and
 * it hides nothing that is refused: a finding is announced ABOVE the rows by
 * `PathwayFlowControls`, and only its full list lives here.
 *
 * ## WHAT IT NEVER DOES
 *
 * It writes no engine key names into the author's decisions. A slot is named by
 * the Pathway adopted on it wherever the catalog answered; the track slug is
 * shown beside it as the identity the exported document carries, not as the
 * thing to be typed. And it offers Remove only where the SERVER says the pinned
 * engine permits a tombstone — everywhere else the way back is Restore
 * inherited, which is a different write and says so.
 */
export function RoutingDisclosure({
  surface,
  state,
  nameOfTrack,
  projectId,
}: {
  surface: PathwayFlowSurface;
  state: ProjectFlowSettings;
  nameOfTrack: (track: string) => string;
  projectId: string;
}) {
  const slotsView = state.settings.find(
    (view) => view.setting === "dynamicTrackSlots",
  );
  return (
    <details
      data-slot="pathway-routing"
      className="rounded-md border border-border bg-card"
    >
      <summary
        aria-label="Routing order and priority for Pathways"
        className="cursor-pointer px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
      >
        Routing order and priority
      </summary>
      <div className="space-y-4 border-t border-border px-4 py-3">
        <section className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">
            Dynamic phase order
          </h4>
          <p className="text-xs text-muted-foreground">
            Read top to bottom. A slot held by an always-assessed pathway runs
            for every patient; the rest are filled by classification, in the
            fallback order below.
            {slotsView ? (
              <>
                {" "}
                <span className="text-foreground">
                  {FLOW_STATE_LABEL[slotsView.state]}.
                </span>
              </>
            ) : null}
          </p>
          {surface.slots.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {surface.legacyShape
                ? // THE LEGACY SHAPE IS NOT AN ABSENCE. This project routes its
                  // dynamic phase through the replaced default-track list and
                  // limit, which have no per-slot order to show — saying it
                  // "authors no slots" here read as "runs nothing", which is
                  // false for a Project running two dynamic tracks per patient.
                  "This project still routes through the replaced selection keys, which have no slot order to show. Its dynamic limit is above; the keys themselves are under Replaced selection keys below. Changing the limit or marking a pathway always assessed writes the slot list that replaces them."
                : "This project authors no dynamic phase slots, and the base pack supplies none the Editor Service could read."}
            </p>
          ) : (
            <ol className="space-y-1 text-sm">
              {surface.slots.map((slot, index) => (
                <li
                  key={index}
                  className="flex flex-wrap items-center gap-x-2 rounded-md border border-border px-3 py-2"
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    {index + 1}
                  </span>
                  {slot.kind === "always" && slot.track !== null ? (
                    <>
                      <Badge tone="accent">Always assessed</Badge>
                      <span className="text-foreground">
                        {nameOfTrack(slot.track)}
                      </span>
                      <code className="text-xs text-muted-foreground">
                        {slot.track}
                      </code>
                    </>
                  ) : (
                    <>
                      <Badge tone="outline">Chosen automatically</Badge>
                      <span className="text-muted-foreground">
                        Filled by classification from the fallback order below.
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}
          {slotsView ? (
            <SettingActions
              surface={surface}
              view={slotsView}
              subject="the dynamic phase slots"
            />
          ) : null}
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">
            Fallback order for automatic slots
          </h4>
          <p className="text-xs text-muted-foreground">
            Lower runs earlier. A slot that is chosen automatically is filled
            from this list, and the Editor Service refuses more automatic slots
            than there are pathways left to fill them.
            {/*
              NO COUNTS HERE, deliberately. `automaticTracks` and
              `autoSlotCount` are both derived from the SAVED document, and the
              slot list above may be mid-edit — a sentence pairing the served
              fallback size with the pending slot count describes a
              configuration that exists in neither. The rule is what an author
              acts on; the numbers are on the slots themselves.
            */}
          </p>
          {state.trackPriorityEntries.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No pathway carries a priority in this project or its base pack.
            </p>
          ) : (
            <ul className="space-y-1">
              {state.trackPriorityEntries.map((view) => (
                <PriorityRow
                  key={view.address}
                  surface={surface}
                  view={view}
                  nameOfTrack={nameOfTrack}
                />
              ))}
            </ul>
          )}
          <WholesalePriorityRemedy surface={surface} state={state} />
        </section>

        <section className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">
            Replaced selection keys
          </h4>
          <p className="text-xs text-muted-foreground">
            The dynamic phase used to be spelled as a default track list and a
            separate limit. The slot list above replaces both, and the pinned
            engine refuses a document that declares the two shapes at once —
            so a project on the slot shape removes them.
          </p>
          <ul className="space-y-1">
            {state.settings
              .filter(
                (view) =>
                  view.setting === "defaultTracks" ||
                  view.setting === "maxDynamicTracks",
              )
              .map((view) => (
                <li
                  key={view.address}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border px-3 py-2"
                >
                  <span className="min-w-0 flex-1 text-sm text-foreground">
                    {view.setting === "defaultTracks"
                      ? "Default track list"
                      : "Separate dynamic limit"}
                  </span>
                  <Badge tone={view.state === "inherited" ? "outline" : "accent"}>
                    {FLOW_STATE_LABEL[view.state]}
                  </Badge>
                  <SettingActions
                    surface={surface}
                    view={view}
                    subject={
                      view.setting === "defaultTracks"
                        ? "the default track list"
                        : "the separate dynamic limit"
                    }
                  />
                </li>
              ))}
          </ul>
        </section>

        {state.findings.length > 0 ? (
          <section className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Why this would not load
            </h4>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {state.findings.map((finding) => (
                <li key={`${finding.code}:${finding.path}`}>
                  <span className="text-foreground">{finding.message}</span>
                  {(finding.subjects ?? []).length > 0
                    ? ` (${(finding.subjects ?? []).join(", ")})`
                    : ""}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <OwnedElsewhere state={state} projectId={projectId} />
      </div>
    </details>
  );
}

/**
 * The way out of a WHOLESALE `track_priority` override, which the per-track
 * rows structurally cannot offer.
 *
 * A draft that stores the mapping at its root — the shape a draft could hold
 * before the typed contract existed — makes every per-track view
 * `authored_above`: the typed write is refused beside it, so each row can only
 * say "revert the override" and point nowhere. The remedy the SERVER publishes
 * is a bare `{setting: "trackPriority", op: "reset"}`, which drops the whole
 * authored mapping and is documented as "the only way out of the legacy shape
 * where a draft stores that mapping wholesale". Round 1 never rendered the root
 * view, so the restore-to-inheritance path this ticket requires was
 * unreachable on exactly the Projects that need it.
 *
 * Rendered only when it is real: an override actually stands, or the Project
 * authors the mapping at its root.
 */
function WholesalePriorityRemedy({
  surface,
  state,
}: {
  surface: PathwayFlowSurface;
  state: ProjectFlowSettings;
}) {
  const root = state.settings.find((view) => view.setting === "trackPriority");
  if (root === undefined) {
    return null;
  }
  const overridden = state.trackPriorityEntries.some(
    (view) => view.state === "authored_above",
  );
  if (!overridden && root.state === "inherited") {
    return null;
  }
  if (!wayBack(root).restore) {
    return null;
  }
  return (
    <div className="space-y-1 rounded-md border border-border px-3 py-2">
      <p className="text-xs text-muted-foreground">
        {overridden
          ? "This project stores the whole priority mapping at one address, which is why the rows above cannot be changed one track at a time. Dropping it returns every priority to the base pack's, after which each track can be set on its own."
          : "This project authors the whole priority mapping. Dropping it returns every priority to the base pack's."}
      </p>
      <SettingActions
        surface={surface}
        view={root}
        subject="this project's whole track priority mapping"
      />
    </div>
  );
}

/** One track's priority: the effective number, and how it got there. */
function PriorityRow({
  surface,
  view,
  nameOfTrack,
}: {
  surface: PathwayFlowSurface;
  view: FlowSettingView;
  nameOfTrack: (track: string) => string;
}) {
  const track = priorityTrackOf(view);
  if (track === null) {
    return null;
  }
  const typed = surface.priorities.get(track);
  const value = typed ?? priorityText(view);
  const refusal = surface.priorityRefusals.get(track) ?? null;
  // A WHOLESALE OVERRIDE MAKES THIS FIELD UNWRITABLE, and the service says so
  // rather than this browser guessing: a typed per-track write beside a value
  // that supplies the whole mapping is refused 422, because one emitted key
  // would have to serve two fields. Leaving the box editable offered an entry
  // whose only outcome was that refusal; the remedy is the whole-mapping reset
  // beneath the list, which `WholesalePriorityRemedy` renders.
  const overridden = view.state === "authored_above";
  // `settled` too: a write offered against a reading whose refresh failed
  // would be computed from a selection the service may already have replaced.
  const blocked =
    !surface.canWrite || surface.saving || !surface.settled || overridden;
  const fieldId = `pathway-priority-${track}`;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border px-3 py-2">
      <label
        htmlFor={fieldId}
        className="min-w-0 flex-1 text-sm text-foreground"
      >
        {nameOfTrack(track)}{" "}
        <code className="text-xs text-muted-foreground">{track}</code>
      </label>
      <Badge tone={view.state === "inherited" ? "outline" : "accent"}>
        {FLOW_STATE_LABEL[view.state]}
      </Badge>
      <Input
        id={fieldId}
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        disabled={blocked}
        aria-label={`Priority for ${nameOfTrack(track)}`}
        {...(refusal !== null ? { "aria-invalid": true } : {})}
        onChange={(event) => surface.editPriority(track, event.target.value)}
        className="h-9 w-20"
      />
      <SettingActions
        surface={surface}
        view={view}
        subject={`the priority for ${nameOfTrack(track)}`}
        track={track}
      />
      {refusal !== null ? (
        <p role="alert" className="basis-full text-xs text-destructive">
          {refusal}
        </p>
      ) : null}
    </li>
  );
}

/**
 * The two ways back, offered only where each is real, and STAGED rather than
 * written.
 *
 * `Remove` writes the pinned engine's tombstone, so the key is absent for the
 * loader rather than present-and-null. It is offered wherever the SERVER's own
 * delete allowlist permits one — including over an INHERITED key, which is the
 * whole point of the governed removal: the base pack supplies `default_tracks`,
 * and a Project on the slot shape removes it.
 *
 * `Restore inherited` drops this Project's own value or tombstone. It has
 * nothing to do while a setting is already inherited, so it is not rendered
 * there — ADR 0001's no-dead-controls rule.
 *
 * Neither is written on its own. The whole pending set is one revisioned
 * request, because the loader judges a DOCUMENT: see `usePathwayFlow`.
 */
function SettingActions({
  surface,
  view,
  subject,
  track = null,
}: {
  surface: PathwayFlowSurface;
  view: FlowSettingView;
  subject: string;
  track?: string | null;
}) {
  const ways = wayBack(view);
  if (view.state === "authored_above") {
    return (
      <span className="text-xs text-muted-foreground">
        Supplied by a wholesale draft override at an ancestor address. Revert
        that override to author this setting on its own.
      </span>
    );
  }
  if (!ways.restore && !ways.remove) {
    return null;
  }
  // `settled` too: a write offered against a reading whose refresh failed
  // would be computed from a selection the service may already have replaced.
  const blocked = !surface.canWrite || surface.saving || !surface.settled;
  const pending = surface.staged.get(stagedKey(view.setting, track))?.op ?? null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {ways.restore ? (
        <Button
          size="sm"
          variant="outline"
          disabled={blocked}
          aria-pressed={pending === "reset"}
          // NAMED WITH ITS SUBJECT, AND WITH THE VISIBLE TEXT IT CURRENTLY
          // SHOWS. This disclosure renders one of these per setting and per
          // track, so a control listing was five buttons called "Restore
          // inherited" and three called "Remove" — and on a Project carrying a
          // wholesale priority override, two of them sit in the same section.
          // The name must still CONTAIN what a sighted user reads (WCAG 2.5.3),
          // and that text changes once the change is staged: a fixed "Restore
          // inherited: …" stopped matching the moment the button began reading
          // "Restoring on save", leaving speech input with nothing to say.
          aria-label={
            pending === "reset"
              ? `Restoring on save: ${subject}`
              : `Restore inherited: ${subject}`
          }
          onClick={() =>
            surface.stage({ op: "reset", setting: view.setting, track, subject })
          }
        >
          {pending === "reset" ? "Restoring on save" : "Restore inherited"}
        </Button>
      ) : null}
      {ways.remove ? (
        <Button
          size="sm"
          variant="outline"
          disabled={blocked}
          aria-pressed={pending === "delete"}
          aria-label={
            pending === "delete"
              ? `Removing on save: ${subject}`
              : `Remove ${subject}`
          }
          onClick={() =>
            surface.stage({
              op: "delete",
              setting: view.setting,
              track,
              subject,
            })
          }
        >
          {pending === "delete" ? "Removing on save" : "Remove"}
        </Button>
      ) : null}
    </span>
  );
}

/**
 * The `flow_config.yaml` regions this surface does NOT own, and who does.
 *
 * The ticket's own requirement — "link BG, closing and Safety controls to their
 * owners instead of exposing overlapping generic flow fields" — and the
 * contract's reason for publishing the ownership table at all: a client that
 * knows only its own keys cannot explain why a neighbouring one is read-only.
 * The final-questioning pair is stricter than ownership and says so: it has no
 * draft address for any surface, and its state is a governed action on Safety.
 */
function OwnedElsewhere({
  state,
  projectId,
}: {
  state: ProjectFlowSettings;
  projectId: string;
}) {
  type OtherOwner = Exclude<FlowRegionOwner, "flow">;
  // THE OWNERS, NOT THEIR ADDRESSES. `FlowOwnershipEntry.path` is a
  // `flow_config.yaml` dotted address, and #488's amendment removes a dotted
  // address from every Project route — `projectPayloadBoundary` fails this file
  // if one is spread into text. What an author can act on is WHO owns the
  // region and where that owner's control is, so the set of owners is what this
  // read reduces to.
  const owners = new Set<OtherOwner>();
  for (const entry of state.contract.owned) {
    if (entry.owner === "flow") {
      continue;
    }
    owners.add(entry.owner);
  }
  if (owners.size === 0) {
    return null;
  }
  const destinations: Record<
    OtherOwner,
    { label: string; to: string | null; note: string }
  > = {
    background_profile: {
      label: "Background interview profile",
      to: `/projects/${projectId}/config/bg-sets`,
      note: "The interview variant and its categories are decided by the background profile this project pins.",
    },
    closing_conversation: {
      label: "Closing conversation",
      to: null,
      note: "The closing track and its registration are decided by the closing conversation this project pins. Its Project control is delivered by issue #867.",
    },
    final_questioning: {
      label: "Final safety questioning",
      to: `/projects/${projectId}/config/safety`,
      note: "The final safety track is a governed action with its own confirmation and audit. It has no draft address on any surface, including this one.",
    },
  };

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-medium text-foreground">
        Decided elsewhere in this project
      </h4>
      <ul className="space-y-1 text-xs text-muted-foreground">
        {[...owners].map((owner) => {
          const destination = destinations[owner];
          return (
            <li key={owner}>
              {destination.to !== null ? (
                <Link
                  to={destination.to}
                  className="font-medium text-foreground underline underline-offset-2"
                >
                  {destination.label}
                </Link>
              ) : (
                <span className="font-medium text-foreground">
                  {destination.label}
                </span>
              )}{" "}
              — {destination.note}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
